import React from 'react';
import { Cell, ColumnInterface, Row } from 'react-table';
import cx from 'classnames';

import { IconFile, Tooltip } from '@trqk/components';
import { locale, getGapAnalysisStatementSelectionLogic } from '@trqk/utils';

import { TrackNameCell } from '.';
import { subRowHeight, summarizeRowsAbove } from '../../constants';
import { PerformancesTableButton } from '../../../../nav';
import { URL_PARAMS, MODAL_IDS } from '../../../../../constants';
import { useModal } from '../../../../../hooks';
import { getFeatureFlags } from '../../../../../featureFlags';

interface GroupedTrackNameCellProps {
    cell: Cell<TrqkGapChannelAudio, GapReportAudioTrackNameCellValue>;
    column: ColumnInterface<TrqkGapChannelAudio>;
    row: Row<TrqkGapChannelAudio>;
}

const labelClasses = 'respond-to-mouse leading-tight break-words px-1 text-xs';
const iconWidth = 24;

/**
 * Displays summary information about a grouped row in the ViewGapAnalysisTable. If there is only
 * a single subRow, it is displayed with the regular <TrackNameCell /> component.
 * @param props - {@link GroupedTrackNameCellProps}
 * @returns JSX
 */
export function GroupedTrackNameCell({
    cell,
    column,
    row,
}: GroupedTrackNameCellProps): JSX.Element {
    if (row.subRows.length <= summarizeRowsAbove) {
        return <TrackNameCell cell={cell} column={column} />;
    }

    const { gapAnalysisLinksToPerformancesTable } = getFeatureFlags();
    const width = (column?.width as number) ?? 100;
    const finalWidth = width - iconWidth - 10;
    const matchedWorkTitle = cell?.value?.matchedWorkTitle ?? null;
    const statementId = cell?.value?.statementId ?? null;

    const statementSelectionLogic =
        getGapAnalysisStatementSelectionLogic(statementId);

    // const channel = cell?.value?.channel ?? null;
    const matchLabel = `${row.subRows.length} ${locale.enUS.GAP_ANALYSIS.REPORT_VIEWER_TABLE.CELLS.GROUPED_TRACK_NAME.MATCHES}`;

    if (matchedWorkTitle === null) {
        return (
            <div className="flex items-center" style={{ height: subRowHeight }}>
                <p
                    className="respond-to-mouse leading-tight clamp-2-lines-break-words text-xs"
                    style={{
                        width: finalWidth,
                    }}
                >
                    {matchLabel}
                </p>
            </div>
        );
    }

    const { remove: removeModal } = useModal();
    const titleLabel = `${matchedWorkTitle} (${matchLabel})`;

    let cellContent = (
        <div
            className={cx(
                labelClasses,
                'font-medium text-black clamp-2-lines-break-words text-left',
            )}
            style={{
                width: finalWidth,
            }}
        >
            {titleLabel}
        </div>
    );

    if (gapAnalysisLinksToPerformancesTable) {
        cellContent = (
            <PerformancesTableButton
                onClick={() => {
                    removeModal(MODAL_IDS.VIEW_GAP_ANALYSIS, {
                        canceled: true,
                        success: false,
                    });
                }}
                urlSearchParams={[
                    {
                        key: URL_PARAMS.WORK_TITLE,
                        value: matchedWorkTitle,
                    },
                ]}
                statementSelectionLogic={statementSelectionLogic}
            >
                <div
                    className={cx(
                        labelClasses,
                        'font-medium text-black clamp-2-lines-break-words underline text-left',
                    )}
                    style={{
                        width: finalWidth,
                    }}
                >
                    {titleLabel}
                </div>
            </PerformancesTableButton>
        );
    }

    return (
        <Tooltip
            message={
                <div>
                    <p className="font-medium">{titleLabel}</p>
                </div>
            }
            position="right"
            minWidth={250}
            maxWidth={250}
        >
            <div className="flex items-center" style={{ height: subRowHeight }}>
                <IconFile addClasses="mx-1" fillColor="primary-500" />
                {cellContent}
            </div>
        </Tooltip>
    );
}
